import React, { useEffect, useRef } from "react";
import { Search, Clock, Command } from "lucide-react";
import { useCommandPalette } from "@/hooks/navigation/useCommandPalette";
import { keyboardShortcuts } from "@/utils/navigation/keyboardShortcuts";
import { cn } from "@/lib/utils";
import DOMPurify from "dompurify";

/**
 * Global command palette for quick navigation and actions
 */
export const CommandPalette: React.FC = () => {
  const {
    isOpen,
    query,
    setQuery,
    searchResults,
    selectedIndex,
    setSelectedIndex,
    recentActions,
    executeCommand,
    close,
  } = useCommandPalette();

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      close();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-[10vh]"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-palette-title"
    >
      <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a command or search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none placeholder:text-muted-foreground text-foreground"
              aria-label="Command palette search"
              aria-expanded={isOpen}
              aria-controls="command-palette-results"
              role="combobox"
              aria-activedescendant={searchResults[selectedIndex]?.id}
            />
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div
          ref={resultsRef}
          id="command-palette-results"
          className="max-h-96 overflow-y-auto"
          role="listbox"
          aria-label="Command palette results"
        >
          {searchResults.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              {query ? "No commands found" : "Type to search commands..."}
            </div>
          ) : (
            <div className="py-2">
              {searchResults.map((result, index) => (
                <CommandItem
                  key={result.id}
                  result={result}
                  isSelected={index === selectedIndex}
                  onClick={() => executeCommand(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="bg-muted px-1 rounded">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="bg-muted px-1 rounded">Enter</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="bg-muted px-1 rounded">Esc</kbd>
                Close
              </span>
            </div>
            {recentActions.length > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Recent actions available
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual command item component
 */
interface CommandResult {
  id: string;
  shortcut?: string[];
  icon?: string;
  highlightedTitle: string;
  highlightedDescription?: string;
  category?: string;
}

interface CommandItemProps {
  result: CommandResult;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

const CommandItem: React.FC<CommandItemProps> = ({
  result,
  isSelected,
  onClick,
  onMouseEnter,
}) => {
  const shortcutText = result.shortcut
    ? keyboardShortcuts.formatShortcut(result.shortcut)
    : null;

  return (
    <div
      id={result.id}
      className={cn(
        "flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors",
        isSelected ? "bg-accent" : "hover:bg-accent/50",
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      role="option"
      aria-selected={isSelected}
    >
      {/* Icon */}
      <div className="w-5 h-5 flex items-center justify-center text-muted-foreground">
        {result.icon && (
          <div className="w-4 h-4">
            {/* This would be replaced with actual icon component */}
            <div className="w-4 h-4 bg-muted-foreground/20 rounded" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div
          className="font-medium text-foreground"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(result.highlightedTitle, {
              ALLOWED_TAGS: ["span", "mark", "strong", "em"],
              ALLOWED_ATTR: ["class"],
              FORBID_TAGS: ["script", "object", "embed"],
              FORBID_ATTR: ["onclick", "onerror", "onload"],
              KEEP_CONTENT: true,
            }),
          }}
        />
        {result.highlightedDescription && (
          <div
            className="text-sm text-muted-foreground truncate"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(result.highlightedDescription, {
                ALLOWED_TAGS: ["span", "mark", "strong", "em"],
                ALLOWED_ATTR: ["class"],
                FORBID_TAGS: ["script", "object", "embed"],
                FORBID_ATTR: ["onclick", "onerror", "onload"],
                KEEP_CONTENT: true,
              }),
            }}
          />
        )}
      </div>

      {/* Category */}
      {result.category && (
        <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
          {result.category}
        </div>
      )}

      {/* Keyboard shortcut */}
      {shortcutText && (
        <div className="text-xs text-muted-foreground font-mono">
          {shortcutText}
        </div>
      )}
    </div>
  );
};
